"use client"

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import { useEffect, useRef } from "react"

import {
  Alignment,
  AutoLink,
  Bold,
  ClassicEditor,
  Code,
  Font,
  GeneralHtmlSupport,
  Heading,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  Strikethrough,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline,
} from "ckeditor5"

import "ckeditor5/ckeditor5.css"
const LICENSE_KEY = "GPL"
const CKBOX_TOKEN_URL = ""

interface CKEditorProps {
  value: string
  onChange: (data: string) => void
  onEditorReady?: (editor: ClassicEditor) => void
}

class MediaUploadAdapter {
  private loader: any

  constructor(loader: any) {
    this.loader = loader
  }

  async upload(): Promise<{ default: string }> {
    const file = await this.loader.file
    const formData = new FormData()
    formData.append("files", file)

    const token = typeof localStorage !== "undefined" ? localStorage.getItem("caocaoAccessToken") : null
    const headers: HeadersInit = {}
    if (token) {
      headers["Authorization"] = `Bearer ${token}`
    }

    const response = await fetch(`${ADMIN_API_CONFIG.API_URL}/api/upload`, {
      method: "POST",
      headers,
      body: formData,
    })

    if (!response.ok) {
      throw new Error("Upload failed")
    }

    const data = await response.json()
    if (!data?.[0]?.url) {
      throw new Error("Invalid upload response")
    }

    return { default: `${ADMIN_API_CONFIG.API_URL}${data[0].url}` }
  }

  abort() {}
}

const CKEditorComponent = ({ value, onChange, onEditorReady }: CKEditorProps) => {
  const editorRef = useRef<ClassicEditor | null>(null)

  // Sync external value changes into editor (e.g. when post data loads)
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const currentData = editorRef.current.getData()
      if (currentData !== value) {
        editorRef.current.setData(value)
      }
    }
  }, [value])

  return (
    <div>
      <CKEditor
        editor={ClassicEditor}
        onReady={(editor: ClassicEditor) => {
          editorRef.current = editor

          // Use custom upload adapter so drag/paste images are uploaded to the server
          // (prevents temporary blob URLs that break after page reload)
          const fileRepository = (editor.plugins.get("FileRepository") as any)
          if (fileRepository) {
            fileRepository.createUploadAdapter = (loader: any) => new MediaUploadAdapter(loader)
          }

          if (editor && value) {
            editor.setData(value)
          }

          onEditorReady?.(editor)
        }}
        onChange={(event: any, editor: ClassicEditor) => {
          const data = editor.getData()
          onChange(data)
        }}
        config={{
          licenseKey: LICENSE_KEY,
          plugins: [
            Font,
            Heading,
            Alignment,
            Image,
            ImageCaption,
            ImageResize,
            ImageTextAlternative,
            ImageToolbar,
            ImageStyle,
            ImageUpload,
            MediaEmbed,
            Link,
            AutoLink,
            Bold,
            Code,
            Italic,
            Strikethrough,
            Underline,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
            List,
            ListProperties,
            GeneralHtmlSupport,
            ...(CKBOX_TOKEN_URL ? ["CKBox"] : []),
            ...(LICENSE_KEY !== "GPL" ? ["SlashCommand"] : []),
          ],
          toolbar: [
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "alignment",
            "mediaEmbed",
            "insertTable",
            "link",
            {
              label: "Fonts",
              icon: false,
              items: ["fontSize", "fontFamily", "fontColor", "fontBackgroundColor"],
            },
            {
              label: "Lists",
              icon: false,
              items: ["bulletedList", "numberedList"],
            },
          ],
          list: {},
          heading: {
            options: [
              { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
              { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
              { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
            ],
          },
          fontFamily: {
            options: [
              "default",
              "Arial, Helvetica, sans-serif",
              "Courier New, Courier, monospace",
              "Georgia, serif",
              "Lucida Sans Unicode, Lucida Grande, sans-serif",
              "Tahoma, Geneva, sans-serif",
              "Times New Roman, Times, serif",
              "Trebuchet MS, Helvetica, sans-serif",
              "Verdana, Geneva, sans-serif",
              "Amazone",
              "Open Sans, sans-serif",
            ],
          },
          fontSize: {
            options: [12, 14, 16, 18, 20, 22, 24, 28, 32, 36],
            supportAllValues: true,
          },
          alignment: {
            options: ["left", "center", "right", "justify"],
          },
          image: {
            toolbar: [
              "imageStyle:alignLeft",
              "imageStyle:alignCenter",
              "imageStyle:alignRight",
              "|",
              "imageTextAlternative",
              "toggleImageCaption",
              "linkImage",
            ],
            styles: {
              options: [
                {
                  name: "alignLeft",
                  title: "Căn trái",
                  icon: "left",
                  className: "image-align-left",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignCenter",
                  title: "Căn giữa",
                  icon: "center",
                  className: "image-align-center",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignRight",
                  title: "Căn phải",
                  icon: "right",
                  className: "image-align-right",
                  modelElements: ["imageBlock", "imageInline"],
                },
                "side",
              ],
            },
          },
          mediaEmbed: {
            previewsInData: true,
            providers: [
              /* ===== YouTube ===== */
              {
                name: "YouTube",
                url: [
                  /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([\w-]+)/,
                  /^https?:\/\/(?:www\.)?youtu\.be\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([\w-]+)/,
                ],
                html: (match: RegExpMatchArray) => {
                  const id = match[match.length - 1] || ""
                  if (!id) return ""
                  const src = `https://www.youtube.com/embed/${id}?rel=0`
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="${src}" ` +
                    `style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" ` +
                    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" ` +
                    `allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },

              /* ===== Facebook ===== */
              {
                name: "Facebook",
                url: [
                  /^https?:\/\/(?:www|m)\.facebook\.com\/[^\/]+\/videos\/(\d+)/,
                  /^https?:\/\/(?:www|m)\.facebook\.com\/watch\/\?v=(\d+)/,
                  /^https?:\/\/fb\.watch\/[\w-]+\/?/,
                ],
                html: (match: RegExpMatchArray) => {
                  const href = encodeURIComponent(match[0])
                  const src = `https://www.facebook.com/plugins/video.php?href=${href}&show_text=false&width=1280`
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="${src}" ` +
                    `style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;overflow:hidden;" ` +
                    `scrolling="no" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" ` +
                    `allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },

              /* ===== TikTok ===== */
              {
                name: "TikTok",
                url: [/^https?:\/\/(?:www\.)?tiktok\.com\/@[\w\.-]+\/video\/(\d+)/],
                html: (match: RegExpMatchArray) => {
                  const videoId = match[1]
                  if (!videoId) return ""
                  const src = `https://www.tiktok.com/embed/v2/${videoId}`
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="${src}" ` +
                    `style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" ` +
                    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" ` +
                    `allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },

              /* ===== Instagram ===== */
              {
                name: "Instagram",
                url: [
                  /^https?:\/\/(?:www\.)?instagram\.com\/p\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?instagram\.com\/reel\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?instagram\.com\/tv\/([\w-]+)/,
                ],
                html: (match: RegExpMatchArray) => {
                  const shortcode = match[1]
                  if (!shortcode) return ""
                  const src = `https://www.instagram.com/p/${shortcode}/embed/`
                  return (
                    `<div style="position:relative;max-width:100%;overflow:hidden;">` +
                    `<iframe src="${src}" ` +
                    `style="width:100%;height:550px;border:0;overflow:hidden;" ` +
                    `frameborder="0" allowtransparency="true" ` +
                    `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"></iframe>` +
                    `</div>`
                  )
                },
              },

              /* ===== Vimeo ===== */
              {
                name: "Vimeo",
                url: /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/,
                html: (match: RegExpMatchArray) => {
                  const id = match[1]
                  return id
                    ? `<div style="position:relative;padding-bottom:56.25%;height:0;">
               <iframe src="https://player.vimeo.com/video/${id}"
                 style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
                 frameborder="0" allow="autoplay; fullscreen; picture-in-picture"
                 allowfullscreen></iframe>
             </div>`
                    : ""
                },
              },

              /* ===== Dailymotion ===== */
              {
                name: "Dailymotion",
                url: /^https?:\/\/(?:www\.)?dailymotion\.com\/video\/([\w-]+)/,
                html: (match: RegExpMatchArray) => {
                  const id = match[1]
                  return id
                    ? `<div style="position:relative;padding-bottom:56.25%;height:0;">
               <iframe src="https://www.dailymotion.com/embed/video/${id}"
                 style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
                 frameborder="0" allow="autoplay; fullscreen; picture-in-picture"
                 allowfullscreen></iframe>
             </div>`
                    : ""
                },
              },
              {
                name: "GenericPreview",
                url: /^https?:\/\/.+/,
                html: (match: RegExpMatchArray) => {
                  const href = match[0]

                  // Nhúng thử iframe (nếu site cho phép)
                  return `
                  <div class="generic-embed" style="max-width:100%;border:1px solid #ddd;border-radius:8px;overflow:hidden;">
                    <iframe
                      src="${href}"
                      style="width:100%;height:400px;border:0;"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    ></iframe>
                  </div>`
                },
              },
            ],
          },

          table: {
            contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],

            tableProperties: {},

            tableCellProperties: {},
          },
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: "https://",
          },
          ckbox: {
            tokenUrl: CKBOX_TOKEN_URL,
          },
          htmlSupport: {
            allow: [
              { name: "figure", attributes: true, classes: true, styles: true },
              { name: "figcaption", attributes: true, classes: true, styles: ["all"] },
              { name: "video", attributes: true, classes: true, styles: true },
              { name: "audio", attributes: true, classes: true, styles: true },
              { name: "source", attributes: true, classes: true, styles: true },
              { name: "iframe", attributes: true, classes: true, styles: true },
              { name: "a", attributes: true, classes: true, styles: true },
              { name: "img", attributes: true, classes: true, styles: true },
              { name: "div", attributes: true, classes: true, styles: true },
            ],
            disallow: [],
          },
        }}
      />
    </div>
  )
}

export default CKEditorComponent
